/**
 * BioTune Theme Manager
 * Comprehensive theme management with localStorage persistence and OS preference detection
 */

import { useState, useEffect } from 'react';
import { themeConfig, themeStorageKeys, themeMediaQueries } from './theme-config';

export type Theme = 'dark' | 'light' | 'system';

export interface ThemeManagerOptions {
  defaultTheme?: Theme;
  enableTransitions?: boolean;
  enableSystemPreferenceDetection?: boolean;
  onThemeChange?: (theme: Theme) => void;
}

export class ThemeManager {
  private currentTheme: Theme;
  private systemPreference: 'dark' | 'light';
  private mediaQueryList: MediaQueryList | null = null;
  private options: ThemeManagerOptions;
  private isInitialized = false;

  constructor(options: ThemeManagerOptions = {}) {
    this.options = {
      defaultTheme: 'system',
      enableTransitions: true,
      enableSystemPreferenceDetection: true,
      onThemeChange: () => {},
      ...options
    };

    this.currentTheme = this.loadThemePreference();
    this.systemPreference = this.detectSystemPreference();
  }

  /**
   * Initialize the theme manager
   */
  public initialize(): void {
    if (this.isInitialized) return;

    this.applyTheme(this.getEffectiveTheme());
    this.setupSystemPreferenceListener();
    this.isInitialized = true;
  }

  /**
   * Get the current theme
   */
  public getCurrentTheme(): Theme {
    return this.currentTheme;
  }

  /**
   * Get the effective theme (resolved from 'system' if needed)
   */
  public getEffectiveTheme(): 'dark' | 'light' {
    if (this.currentTheme === 'system') {
      return this.systemPreference;
    }
    return this.currentTheme;
  }

  /**
   * Set the theme
   */
  public setTheme(theme: Theme): void {
    if (this.currentTheme === theme) return;

    const previousTheme = this.currentTheme;
    this.currentTheme = theme;
    
    this.saveThemePreference();
    this.applyTheme(this.getEffectiveTheme());
    
    if (this.options.onThemeChange) {
      this.options.onThemeChange(theme);
    }

    // Dispatch custom event for other components to listen to
    window.dispatchEvent(new CustomEvent('biotune-theme-change', {
      detail: { theme, previousTheme }
    }));
  }

  /**
   * Toggle between dark and light themes
   */
  public toggleTheme(): void {
    const effectiveTheme = this.getEffectiveTheme();
    this.setTheme(effectiveTheme === 'dark' ? 'light' : 'dark');
  }

  /**
   * Get system preference
   */
  public getSystemPreference(): 'dark' | 'light' {
    return this.systemPreference;
  }

  /**
   * Check if system preference detection is supported
   */
  public isSystemPreferenceSupported(): boolean {
    return window.matchMedia !== undefined;
  }

  /**
   * Get theme configuration
   */
  public getThemeConfig() {
    return themeConfig;
  }

  /**
   * Load theme preference from localStorage
   */
  private loadThemePreference(): Theme {
    try {
      const stored = localStorage.getItem(themeStorageKeys.theme);
      if (stored && (stored === 'dark' || stored === 'light' || stored === 'system')) {
        return stored as Theme;
      }
    } catch (error) {
      console.warn('Failed to load theme preference from localStorage:', error);
    }
    return this.options.defaultTheme || 'system';
  }

  /**
   * Save theme preference to localStorage
   */
  private saveThemePreference(): void {
    try {
      localStorage.setItem(themeStorageKeys.theme, this.currentTheme);
      localStorage.setItem(themeStorageKeys.timestamp, Date.now().toString());
    } catch (error) {
      console.warn('Failed to save theme preference to localStorage:', error);
    }
  }

  /**
   * Detect system color scheme preference
   */
  private detectSystemPreference(): 'dark' | 'light' {
    if (!this.isSystemPreferenceSupported()) {
      return 'light'; // Default fallback
    }

    try {
      const darkModeQuery = window.matchMedia(themeMediaQueries.prefersDark);
      return darkModeQuery.matches ? 'dark' : 'light';
    } catch (error) {
      console.warn('Failed to detect system preference:', error);
      return 'light';
    }
  }

  /**
   * Setup system preference change listener
   */
  private setupSystemPreferenceListener(): void {
    if (!this.options.enableSystemPreferenceDetection || !this.isSystemPreferenceSupported()) {
      return;
    }

    try {
      const darkModeQuery = window.matchMedia(themeMediaQueries.prefersDark);
      
      // Modern browsers
      if (darkModeQuery.addEventListener) {
        darkModeQuery.addEventListener('change', this.handleSystemPreferenceChange.bind(this));
      } else if ((darkModeQuery as any).addListener) {
        // Legacy browsers
        (darkModeQuery as any).addListener(this.handleSystemPreferenceChange.bind(this));
      }

      this.mediaQueryList = darkModeQuery;
    } catch (error) {
      console.warn('Failed to setup system preference listener:', error);
    }
  }

  /**
   * Handle system preference changes
   */
  private handleSystemPreferenceChange(event: MediaQueryListEvent): void {
    const newPreference = event.matches ? 'dark' : 'light';
    
    if (this.systemPreference !== newPreference) {
      this.systemPreference = newPreference;
      
      // Only apply if current theme is 'system'
      if (this.currentTheme === 'system') {
        this.applyTheme(this.systemPreference);
        
        if (this.options.onThemeChange) {
          this.options.onThemeChange('system');
        }

        window.dispatchEvent(new CustomEvent('biotune-system-preference-change', {
          detail: { preference: newPreference }
        }));
      }
    }
  }


  /**
   * Apply theme to the document
   */
  private applyTheme(theme: 'dark' | 'light'): void {
    const root = document.documentElement;
    
    // Remove existing theme classes
    root.classList.remove('theme-dark', 'theme-light', 'dark');
    
    // Add new theme class
    root.classList.add(`theme-${theme}`);
    
    // Add Tailwind dark class for dark: variants to work
    if (theme === 'dark') {
      root.classList.add('dark');
    }
  }

  /**
   * Cleanup resources
   */
  public destroy(): void {
    if (this.mediaQueryList) {
      try {
        if (this.mediaQueryList.removeEventListener) {
          this.mediaQueryList.removeEventListener('change', this.handleSystemPreferenceChange.bind(this));
        } else if ((this.mediaQueryList as any).removeListener) {
          (this.mediaQueryList as any).removeListener(this.handleSystemPreferenceChange.bind(this));
        }
      } catch (error) {
        console.warn('Failed to remove system preference listener:', error);
      }
    }
    
    const themeStyle = document.getElementById('biotune-theme-variables');
    if (themeStyle) {
      themeStyle.remove();
    }
    
    this.isInitialized = false;
  }
}

/**
 * Global theme manager instance
 */
let globalThemeManager: ThemeManager | null = null;

/**
 * Initialize the global theme manager
 */
export function initializeThemeManager(options?: ThemeManagerOptions): ThemeManager {
  if (!globalThemeManager) {
    globalThemeManager = new ThemeManager(options);
    globalThemeManager.initialize();
  }
  return globalThemeManager;
}

/**
 * Get the global theme manager instance
 */
export function getThemeManager(): ThemeManager {
  if (!globalThemeManager) {
    return initializeThemeManager();
  }
  return globalThemeManager;
}

export function resetThemeManager(): void {
  if (globalThemeManager) {
    globalThemeManager.destroy();
    globalThemeManager = null;
  }
}

/**
 * Convenience functions for theme management
 */
export const themeUtils = {
  /**
   * Get current theme
   */
  getCurrentTheme(): Theme {
    return getThemeManager().getCurrentTheme();
  },

  /**
   * Get effective theme (resolved from 'system')
   */
  getEffectiveTheme(): 'dark' | 'light' {
    return getThemeManager().getEffectiveTheme();
  },

  /**
   * Set theme
   */
  setTheme(theme: Theme): void {
    getThemeManager().setTheme(theme);
  },

  /**
   * Toggle theme
   */
  toggleTheme(): void {
    getThemeManager().toggleTheme();
  },

  /**
   * Check if current theme is dark
   */
  isDark(): boolean {
    return getThemeManager().getEffectiveTheme() === 'dark';
  },

  /**
   * Check if current theme is light
   */
  isLight(): boolean {
    return getThemeManager().getEffectiveTheme() === 'light';
  },

  /**
   * Get system preference
   */
  getSystemPreference(): 'dark' | 'light' {
    return getThemeManager().getSystemPreference();
  }
};

/**
 * React Hook for theme management
 */
export function useTheme() {
  const [theme, setTheme] = useState<Theme>(themeUtils.getCurrentTheme());
  const [effectiveTheme, setEffectiveTheme] = useState<'dark' | 'light'>(themeUtils.getEffectiveTheme());

  useEffect(() => {
    const handleThemeChange = (event: CustomEvent) => {
      setTheme(event.detail.theme);
      setEffectiveTheme(themeUtils.getEffectiveTheme());
    };

    const handleSystemPreferenceChange = () => {
      setEffectiveTheme(themeUtils.getEffectiveTheme());
    };

    window.addEventListener('biotune-theme-change', handleThemeChange as any);
    window.addEventListener('biotune-system-preference-change', handleSystemPreferenceChange as any);

    return () => {
      window.removeEventListener('biotune-theme-change', handleThemeChange as any);
      window.removeEventListener('biotune-system-preference-change', handleSystemPreferenceChange as any);
    };
  }, []);

  return {
    theme,
    effectiveTheme,
    setTheme: themeUtils.setTheme,
    toggleTheme: themeUtils.toggleTheme,
    isDark: themeUtils.isDark(),
    isLight: themeUtils.isLight(),
    systemPreference: themeUtils.getSystemPreference()
  };
}

export default ThemeManager;
